import { useState, useEffect, useRef, useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  flexRender,
  createColumnHelper,
  type SortingState,
  type ColumnFiltersState,
  type VisibilityState,
  type ColumnOrderState,
  type Table,
} from "@tanstack/react-table";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  GripVertical,
  SlidersHorizontal,
  Search,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
} from "lucide-react";
import { useEdrsQuery } from "@/lib/queries";
import { useFilteredInfringements } from "@/hooks/useFilteredInfringements";
import { useSidePanel } from "@/contexts/SidePanelContext";
import { WidgetShell } from "@/components/dashboard/WidgetShell";
import { InfringementDetail } from "./InfringementDetail";
import { InfringementActionBadge, TYPE_CONFIG } from "./badges";
import { cn, fmtDateTime } from "@/lib/utils";
import type { Infringement, InfringementSourceAntiPiracy, Edr } from "@/types/domain";

const col = createColumnHelper<Infringement>();

const COL_LABELS: Record<string, string> = {
  external_id: "ID",
  lastseen: "Last Seen",
  event: "Event",
  pirate_service: "Pirate service",
  tenant_id: "Tenant",
  infringement_type: "Type",
  edr_id: "Source",
  status: "Status",
  action: "Action",
  channel: "Channel",
};

function SortableColRow({
  col,
}: {
  col: ReturnType<Table<Infringement>["getAllLeafColumns"]>[number];
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: col.id,
  });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "flex items-center gap-2 px-2 py-1.5 rounded",
        isDragging ? "bg-surface-elevated shadow-lg z-50" : "hover:bg-surface-elevated",
      )}
    >
      <span
        {...attributes}
        {...listeners}
        className="text-muted/40 hover:text-muted cursor-grab active:cursor-grabbing touch-none"
      >
        <GripVertical className="h-3.5 w-3.5" />
      </span>
      <input
        type="checkbox"
        checked={col.getIsVisible()}
        onChange={col.getToggleVisibilityHandler()}
        className="accent-brand-cyan h-3 w-3 shrink-0 cursor-pointer"
      />
      <span className="flex-1 text-xs text-secondary">{COL_LABELS[col.id] ?? col.id}</span>
    </div>
  );
}

function ColumnSettings({ table }: { table: Table<Infringement> }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const orderedCols = table.getAllLeafColumns();

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const order = table.getState().columnOrder.length
      ? table.getState().columnOrder
      : orderedCols.map((c) => c.id);
    const from = order.indexOf(active.id as string);
    const to = order.indexOf(over.id as string);
    table.setColumnOrder(arrayMove(order, from, to));
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "p-1.5 rounded-md border transition-colors cursor-pointer",
          open
            ? "text-primary bg-surface-elevated border-border"
            : "text-muted border-transparent hover:text-primary hover:bg-surface-elevated hover:border-border",
        )}
        aria-label="Column settings"
      >
        <SlidersHorizontal className="h-3.5 w-3.5" />
      </button>

      {open && (
        <div className="absolute top-full right-0 mt-1 z-50 w-52 bg-surface border border-border rounded-lg shadow-xl py-2">
          <p className="px-3 pb-1 text-[10px] font-bold uppercase tracking-widest text-muted/50">
            Columns
          </p>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext
              items={orderedCols.map((c) => c.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="px-1">
                {orderedCols.map((col) => (
                  <SortableColRow key={col.id} col={col} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      )}
    </div>
  );
}

const DEFAULT_COLUMN_ORDER = [
  "external_id",
  "tenant_id",
  "lastseen",
  "infringement_type",
  "edr_id",
  "action",
  "event",
  "pirate_service",
];

function makeColumns(edrs: Edr[] | undefined) {
  return [
    col.accessor("external_id", {
      header: "ID",
      size: 140,
      cell: (info) => (
        <span className="text-xs font-mono text-secondary truncate">{info.getValue() ?? "—"}</span>
      ),
    }),
    col.accessor("lastseen", {
      header: "Last Seen",
      cell: (info) => (
        <span className="text-xs text-muted tabular-nums">{fmtDateTime(info.getValue())}</span>
      ),
      size: 90,
    }),
    col.accessor("tenant_id", {
      header: "Tenant",
      cell: (info) => (
        <span className="text-xs text-muted tabular-nums">{info.getValue() ?? "—"}</span>
      ),
      size: 50,
    }),
    col.accessor("infringement_type", {
      header: "Type",
      size: 100,
      filterFn: "arrIncludesSome",
      cell: (info) => {
        const label =
          TYPE_CONFIG[info.getValue() as keyof typeof TYPE_CONFIG]?.label ?? info.getValue();
        return <span className="text-xs text-secondary">{label}</span>;
      },
    }),
    col.accessor("edr_id", {
      header: "Source",
      cell: (info) => {
        const label = edrs?.find((e) => e.id === info.getValue())?.label ?? info.getValue();
        return <span className="text-xs text-secondary">{label}</span>;
      },
      size: 100,
    }),
    col.accessor("event", {
      header: "Event",
      cell: (info) => (
        <span className="text-xs text-secondary truncate">{info.getValue() ?? "—"}</span>
      ),
    }),
    col.display({
      id: "pirate_service",
      header: "Pirate service",
      cell: ({ row }) => {
        const svc = (row.original.details as InfringementSourceAntiPiracy).pirate_service;
        return <span className="text-xs text-secondary truncate">{svc ?? "—"}</span>;
      },
    }),
    col.display({
      id: "action",
      header: "Action",
      size: 120,
      cell: ({ row }) => (
        <InfringementActionBadge
          action={row.original.action}
          actionResult={row.original.action_result}
        />
      ),
    }),
  ];
}

function SortIcon({ sorted }: { sorted: false | "asc" | "desc" }) {
  if (sorted === "asc") return <ChevronUp className="h-3 w-3 text-brand-cyan" />;
  if (sorted === "desc") return <ChevronDown className="h-3 w-3 text-brand-cyan" />;
  return <ChevronsUpDown className="h-3 w-3 text-muted/40" />;
}

export function InfringementsTableWidget() {
  const { data: edrs } = useEdrsQuery();
  const { infringements, isPending } = useFilteredInfringements();

  const rows = useMemo(() => {
    return infringements;
  }, [infringements]);

  const columns = useMemo(() => makeColumns(edrs), [edrs]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    event: false,
    pirate_service: false,
  });
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(DEFAULT_COLUMN_ORDER);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { open: openPanel, isOpen: panelOpen, close: closePanel } = useSidePanel();

  useEffect(() => {
    if (!panelOpen) setSelectedId(null);
  }, [panelOpen]);

  function handleRowClick(infringement: Infringement) {
    if (selectedId === infringement.id) {
      closePanel();
      return;
    }
    setSelectedId(infringement.id);
    openPanel(<InfringementDetail infringementId={infringement.id} />, "Infringement Details");
  }

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, globalFilter, columnFilters, columnVisibility, columnOrder },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  return (
    <WidgetShell title="Infringements">
      {/* Header Tools */}
      <div className="shrink-0 flex items-center justify-between gap-4 px-4 py-2 bg-surface border-b border-border">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted/50 pointer-events-none" />
          <input
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            placeholder="Filter infringements..."
            className="w-full pl-9 pr-3 py-2 text-sm bg-surface border border-border rounded-md text-secondary placeholder:text-muted/40 placeholder:italic focus:outline-none focus:ring-1 focus:ring-brand-cyan/50 focus:border-brand-cyan/40 transition-colors"
          />
        </div>

        <div className="flex items-center gap-3">
          <ColumnSettings table={table} />
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto min-h-0">
        <table className="w-full min-w-[800px] text-xs">
          <thead className="sticky top-0 z-10 bg-surface border-b border-border">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((header) => (
                  <th
                    key={header.id}
                    style={{ width: header.getSize() }}
                    className={cn(
                      "px-3 first:pl-4 last:pr-4 py-2.5 text-left text-xs font-bold uppercase tracking-wider text-muted",
                      header.column.getCanSort() && "cursor-pointer select-none hover:text-primary",
                    )}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <div className="flex items-center gap-1">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getCanSort() && (
                        <SortIcon sorted={header.column.getIsSorted()} />
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {isPending ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-8 text-center text-muted/50 text-xs"
                >
                  Loading…
                </td>
              </tr>
            ) : table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-8 text-center text-muted/50 text-xs"
                >
                  No infringements found
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => {
                const isSelected = selectedId === row.original.id;
                const isBlocked = row.original.action_result === "blocked";
                return (
                  <tr
                    key={row.id}
                    onClick={() => handleRowClick(row.original)}
                    className={cn(
                      "border-b border-border/40 border-l-2 transition-colors cursor-pointer",
                      isBlocked
                        ? isSelected
                          ? "bg-red-500/20 border-l-red-600 dark:bg-red-500/30 dark:border-l-red-400"
                          : "bg-red-500/10 border-l-transparent dark:bg-red-500/15 hover:bg-red-500/15 dark:hover:bg-red-500/20"
                        : isSelected
                          ? "bg-surface-elevated border-l-brand-cyan"
                          : "border-l-transparent bg-transparent hover:bg-surface-elevated/60",
                    )}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-3 first:pl-4 last:pr-4 py-2 truncate max-w-0">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="shrink-0 px-4 py-1.5 border-t border-border/50 flex justify-between items-center text-xs text-muted">
        <span>
          {table.getFilteredRowModel().rows.length} / {rows.length} rows
        </span>
      </div>
    </WidgetShell>
  );
}
